/* 
-----------------------------------------------------------------------------
| DATE SELECTION PAGE
-----------------------------------------------------------------------------
- description: its only purpose is to manage the value of booking.date
  and reset other values or navigate to other route if needed
*/
import { FC, useState, useEffect, useRef} from "react";
import { useApp } from "../context/useApp";
import { cache, ROUTES, rumba, metrics } from "../utils/symbols";
import { MonthAvailability, MonthsAvailability } from "../types";
import { DateSelector } from "../parts/date-selector/DateSelector";
import { DEFAULT_DIALOG_HANDLE } from "../utils/symbols";
import { _ } from "../utils/helpers";

interface DateSelectionProps {
    asCalendarWidget?: boolean
    at?: string | null
    from?: string | null
    displayedMonthsQty?: number | null
    value?: string | null
    specialDates?: string[]
}

export const DateSelection: FC<DateSelectionProps> = ({
    asCalendarWidget, 
    at, 
    from, 
    displayedMonthsQty, 
    value,
    specialDates,
}) => {
    // todo: move availability to provider to not loose the cache when changing step
    const [availability, setAvailability] = useState<MonthsAvailability>({})
    const requestedMonths = useRef<{[key: string]: boolean}>({})
    // MonthsAvailability keys -> yyyy-m with january as 1

    const {
        state: { booking, dialogHandles },
        actions: { navigate, genericError }
    } = useApp()

    const calendarDialog = dialogHandles.get('calendar-dialog') ?? DEFAULT_DIALOG_HANDLE

    const handleDateSelection = async (date: string): Promise<void> => {
        // calendar widget behavior
        if (asCalendarWidget) {
            window.parent.location.href = (window as any).bookingEnginePage + `?date=${date}`
            return
        }
        // date cannot be resetted so do not toggle it. just
        // overwrite booking.date value, reset the corresponding data
        // due availability changes and go to next step
        if (booking.date.split('T')[0] !== date) {
            booking.selectDate(date)
            await metrics.dateSelectionEvent(booking.getBookingDataByReference())
        }
        booking.setBookingStep('shift').render()
        navigate(ROUTES.TABLE_LAYOUT)
    }

    const getMonthAvailability = async (date: Date): Promise<void> => {
        
        const month = `${date.getFullYear()}-${date.getMonth() + 1}`
        const availabilityInCache = cache.get('availability',month)
        if (availabilityInCache) {
            setAvailability(months => ({...months, [month]: availabilityInCache}))
            return
        }
        
        calendarDialog.openDialog({ dialog: 'loading', className: 'w-100 df aic jcc loading' })
        const response = await rumba.getMonthAvailability(date)
        calendarDialog.closeDialog()
        if (response.result !== 'ok') {
            genericError(() => getMonthAvailability(date))
            return
        }

        /* when re-requesting availability, the selected booking.date availability
         * could change, so, if does and its not available, unselect it. */
        const bookingDataDateObj = new Date(booking.date)
        if (
            booking.date !== '' &&
            bookingDataDateObj.getFullYear() === date.getFullYear() &&
            bookingDataDateObj.getMonth() === date.getMonth() &&
            response.data[bookingDataDateObj.getDate() - 1] === 0//sold-out
        ) booking.unSelectDate().render()

        setAvailability(months => ({...months, [month]: response.data}))
        cache.add('availability', month, response.data)
    }

    useEffect(() => {

        if (!dialogHandles.has('calendar-dialog')) return
        /* request first two months. (Two months may be visible) 
         * if user go back to date step, re-request availability due to
         * availability changes during booking process */
        const dateToRequest = new Date(booking.date || from || Date.now())
        getMonthAvailability(dateToRequest)
        getMonthAvailability(new Date(dateToRequest.getFullYear(), dateToRequest.getMonth() + 1, 1))

    }, [dialogHandles])

    return (
        <DateSelector
            displayedMonthsQty={displayedMonthsQty ?? 12}
            from={from ?? Date.now()}
            at={at ?? booking.date}
            value={value ?? booking.date}
            monthsAvailability={availability}
            onDateSelected={handleDateSelection}
            onMonthChange={getMonthAvailability}
            specialDates={specialDates}
        />
    )
}